import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@civilgrid/ui/components';
import { useRef } from 'react';
import Icon from '~/components2/Icon';
import { FeatureAnnouncement } from '~/pages/common/FeatureAnnouncements/FeatureAnnouncement';
import { openPylonChatWithFocus } from '~/pylon-widget';

interface HelpDropdownProps {
  /** Controlled open state for the Product Updates modal, lifted to TopNav so it can be mutually exclusive with the notifications feed. */
  productUpdatesOpen: boolean;
  onProductUpdatesOpenChange: (open: boolean) => void;
}

export default function HelpDropdown({ productUpdatesOpen, onProductUpdatesOpenChange }: HelpDropdownProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const chatSelectedRef = useRef(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="Help menu"
            data-testid="top-nav-help-trigger"
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-[6px] text-primary focus-ring"
          >
            <Icon size={28} icon="help2" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          data-testid="top-nav-help-menu"
          onCloseAutoFocus={(event) => {
            // Keeps Radix from pulling focus back to the trigger after the chat takes it.
            if (chatSelectedRef.current) event.preventDefault();
            chatSelectedRef.current = false;
          }}
        >
          <DropdownMenuItem
            aria-label="Product Updates"
            data-testid="top-nav-help-product-updates"
            onSelect={() => onProductUpdatesOpenChange(true)}
          >
            Product Updates
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            aria-label="Chat with Support"
            data-testid="top-nav-help-chat-support"
            onSelect={() => {
              if (triggerRef.current) chatSelectedRef.current = openPylonChatWithFocus(triggerRef.current);
            }}
          >
            Chat with Support
          </DropdownMenuItem>
          <DropdownMenuItem
            aria-label="Help Center, opens in a new tab"
            data-testid="top-nav-help-center"
            onSelect={() => {
              window.open(
                'https://portal.usepylon.com/civilgrid/login?kbUrl=https%3A%2F%2Fcivilgrid.help.usepylon.com%2F',
                '_blank',
              );
            }}
          >
            Help Center
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {productUpdatesOpen && (
        <FeatureAnnouncement
          open={productUpdatesOpen}
          onClose={() => onProductUpdatesOpenChange(false)}
          // The menu item that opened the dialog is gone by close, so focus returns to the trigger instead.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus();
          }}
        />
      )}
    </>
  );
}
